import React, { Component } from 'react';
import { arrayOf, bool, object, func, shape, string } from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { Switch, Route, withRouter } from 'react-router-dom';

import { useRouteConfiguration } from '../context/routeConfigurationContext';
import { propTypes } from '../util/types';
import * as log from '../util/log';
import { canonicalRoutePath } from '../util/routes';
import { useConfiguration } from '../context/configurationContext';
import { isGuestUser, isGuestAllowedPage } from '../util/userHelpers';

import { locationChanged } from '../ducks/routing.duck';
import { logout } from '../ducks/auth.duck';

import { NamedRedirect } from '../components';
import NotFoundPage from '../containers/NotFoundPage/NotFoundPage';

import LoadableComponentErrorBoundary from './LoadableComponentErrorBoundary/LoadableComponentErrorBoundary';
import { hasQueryParam } from '../util/urlHelpers';

const canShowComponent = props => {
  const { isAuthenticated, route } = props;
  const { auth } = route;
  return !auth || isAuthenticated;
};

const callLoadData = props => {
  const { match, location, route, dispatch, logoutInProgress, config } = props;
  const { loadData, name } = route;
  const shouldLoadData =
    typeof loadData === 'function' && canShowComponent(props) && !logoutInProgress;

  if (shouldLoadData) {
    dispatch(loadData(match.params, location.search, config))
      .then(() => {
        if (props.logLoadDataCalls) {
          // This gives good input for debugging issues on live environments, but with test it's not needed.
          console.log(`loadData success for ${name} route`);
        }
      })
      .catch(e => {
        log.error(e, 'load-data-failed', { routeName: name });
      });
  }
};

const setPageScrollPosition = (location, delayed, shouldScrollToTop = true) => {
  if (!location.hash) {
    if (shouldScrollToTop) {
      // No hash, scroll to top
      window.scroll({
        top: 0,
        left: 0,
      });
    }
  } else {
    const el = document.querySelector(location.hash);
    if (el) {
      // Found element from the current page with the given fragment identifier,
      // scrolling to that element.
      //
      // NOTE: This only works on in-app navigation within the same page.
      // If smooth scrolling is needed between different pages, one needs to wait
      //   1. loadData fetch and
      //   2. code-chunk fetch
      // before making el.scrollIntoView call.
      el.scrollIntoView({
        block: 'start',
        behavior: 'smooth',
      });
    } else {
      // A naive attempt to make a delayed call to scrollIntoView
      // Note: 300 milliseconds might not be enough, but adding too much delay
      // might affect user initiated scrolling.
      delayed = window.setTimeout(() => {
        const reTry = document.querySelector(location.hash);
        reTry?.scrollIntoView?.({
          block: 'start',
          behavior: 'smooth',
        });
      }, 300);
    }
  }
};

const handleLocationChanged = (
  dispatch,
  location,
  routeConfiguration,
  delayed,
  shouldScrollToTop = true
) => {
  setPageScrollPosition(location, delayed, shouldScrollToTop);
  const path = canonicalRoutePath(routeConfiguration, location);
  dispatch(locationChanged(location, path));
};

/**
 * RouteComponentRenderer handles loadData calls on client-side.
 * It also checks authentication and redirects unauthenticated users
 * away from routes that are for authenticated users only
 * (aka "auth: true" is set in routeConfiguration.js)
 *
 * This component is a container: it needs to be connected to Redux.
 */
class RouteComponentRenderer extends Component {
  constructor(props) {
    super(props);
    this.guestSignupProcessing = false;
  }

  componentDidMount() {
    const { dispatch, location, routeConfiguration } = this.props;
    this.delayed = null;
    // Check guest user restrictions
    this.checkGuestRestrictions();
    // Calling loadData on initial rendering (on client side).
    callLoadData(this.props);
    handleLocationChanged(dispatch, location, routeConfiguration, this.delayed);
  }

  componentDidUpdate(prevProps) {
    const { dispatch, location, routeConfiguration } = this.props;
    // Call for handleLocationChanged affects store/state
    // and it generates an unnecessary update.
    if (prevProps.location !== this.props.location) {
      const pathnameChanged = prevProps.location.pathname !== this.props.location.pathname;
      const shouldScrollToTop = pathnameChanged || !!this.props.location.hash;

      // Check guest user restrictions on location change
      this.checkGuestRestrictions();
      // Calling loadData after initial rendering (on client side).
      // This makes it possible to use loadData as default client side data loading technique.
      // However it is better to fetch data before location change to avoid "Loading data" state.
      callLoadData(this.props);
      handleLocationChanged(
        dispatch,
        location,
        routeConfiguration,
        this.delayed,
        shouldScrollToTop
      );
    }
  }

  checkGuestRestrictions = () => {
    const { currentUser, isAuthenticated, location, route, dispatch } = this.props;

    // If guest signup completion is pending, skip all restrictions
    const isGuestSignupCompleted = sessionStorage.getItem('guest_signup_completed');
    console.log('🔍 Checking guest restrictions - isGuestSignupCompleted:', isGuestSignupCompleted);
    console.log('🔍 Guest signup processing:', this.guestSignupProcessing);

    if (isGuestSignupCompleted && !this.guestSignupProcessing) {
      console.log('🚀 Triggering guest signup completion...');
      this.handleGuestSignupCompletion();
      return; // Skip restrictions check entirely
    }

    // Skip if guest signup is already processing
    if (this.guestSignupProcessing) {
      return;
    }

    // Only check if user is authenticated and is a guest
    if (isAuthenticated && currentUser && isGuestUser(currentUser)) {
      const currentPage = route.name;
      const pathname = location.pathname;
      const isAllowed = isGuestAllowedPage(currentPage, pathname);
      // If guest is trying to access a non-allowed page, logout immediately
      if (!isAllowed) {
        dispatch(logout());
      }
    }
  };

  handleGuestSignupCompletion = async () => {
    // Prevent multiple executions
    if (this.guestSignupProcessing) {
      return;
    }

    const { dispatch } = this.props;
    const isGuestSignupCompleted = sessionStorage.getItem('guest_signup_completed');
    const newCredentials = sessionStorage.getItem('guest_signup_new_credentials');

    if (isGuestSignupCompleted && newCredentials) {
      try {
        this.guestSignupProcessing = true;
        const credentials = JSON.parse(newCredentials);
        // Import the auth actions
        const { logout, loginWithoutTurnstile } = await import('../ducks/auth.duck');

        // Logout current user and login with new account
        await dispatch(logout());

        await dispatch(loginWithoutTurnstile(credentials.email, credentials.password));

        // Get return URL before clearing
        const returnUrl = sessionStorage.getItem('guest_signup_return_url');

        // Clear the flags AFTER the process is complete
        sessionStorage.removeItem('guest_signup_completed');
        sessionStorage.removeItem('guest_signup_new_credentials');
        sessionStorage.removeItem('guest_signup_return_url');

        // Redirect back to the saved URL if available
        if (returnUrl && window.location.pathname + window.location.search !== returnUrl) {
          window.location.href = returnUrl;
        }
      } catch (error) {
        console.error('Guest signup completion failed:', error);
        // Clear flags on error
        sessionStorage.removeItem('guest_signup_completed');
        sessionStorage.removeItem('guest_signup_new_credentials');
        sessionStorage.removeItem('guest_signup_return_url');
      } finally {
        this.guestSignupProcessing = false;
      }
    }
  };

  componentWillUnmount() {
    if (this.delayed) {
      window.clearTimeout(this.resetTimeoutId);
    }
  }

  render() {
    const { route, match, location, staticContext } = this.props;

    // If location has an iframe query parameter, we are rendering in an iframe.
    const { component: RouteComponent, authPage = 'SignupPage', extraProps } = route;
    const canShow = canShowComponent(this.props);
    if (!canShow) {
      staticContext.unauthorized = true;
    }
    const isIframe = hasQueryParam(location, 'iframe');
    const redirectProps = isIframe ? { search: '?iframe=true' } : {};
    return canShow ? (
      <LoadableComponentErrorBoundary>
        <RouteComponent params={match.params} location={location} {...extraProps} />
      </LoadableComponentErrorBoundary>
    ) : (
      <NamedRedirect
        name={authPage}
        state={{ from: `${location.pathname}${location.search}${location.hash}` }}
        {...redirectProps}
      />
    );
  }
}

RouteComponentRenderer.defaultProps = { staticContext: {} };

RouteComponentRenderer.propTypes = {
  isAuthenticated: bool.isRequired,
  logoutInProgress: bool.isRequired,
  currentUser: propTypes.currentUser,
  route: propTypes.route.isRequired,
  routeConfiguration: arrayOf(propTypes.route).isRequired,
  match: shape({
    params: object.isRequired,
    url: string.isRequired,
  }).isRequired,
  location: shape({
    search: string.isRequired,
  }).isRequired,
  staticContext: object,
  dispatch: func.isRequired,
};

const mapStateToProps = state => {
  const { isAuthenticated, logoutInProgress } = state.auth;
  const { currentUser } = state.user;
  return { isAuthenticated, logoutInProgress, currentUser };
};
const RouteComponentContainer = compose(connect(mapStateToProps))(RouteComponentRenderer);

/**
 * Routes component creates React Router rendering setup.
 * It needs routeConfiguration (named as "routes") through props.
 * Using that configuration it creates navigation on top of page-level
 * components. Essentially, it's something like:
 * <Switch>
 *   <Route render={pageA} />
 *   <Route render={pageB} />
 * </Switch>
 */
const Routes = (props, context) => {
  const routeConfiguration = useRouteConfiguration();
  const config = useConfiguration();
  const { isAuthenticated, logoutInProgress, logLoadDataCalls, currentUser } = props;

  const toRouteComponent = route => {
    const renderProps = {
      isAuthenticated,
      logoutInProgress,
      currentUser,
      route,
      routeConfiguration,
      config,
      logLoadDataCalls,
    };

    // By default, our routes are exact.
    // https://reacttraining.com/react-router/web/api/Route/exact-bool
    const isExact = route.exact != null ? route.exact : true;
    return (
      <Route
        key={route.name}
        path={route.path}
        exact={isExact}
        render={matchProps => (
          <RouteComponentContainer
            {...renderProps}
            match={matchProps.match}
            location={matchProps.location}
            staticContext={matchProps.staticContext}
          />
        )}
      />
    );
  };

  return (
    <Switch>
      {routeConfiguration.map(toRouteComponent)}
      <Route component={NotFoundPage} />
    </Switch>
  );
};

export default withRouter(Routes);
